import { useState } from 'react';
import { Bell, X } from 'lucide-react';
import { useCurrentUser } from '@/api/hooks';
import { usePushOptIn } from '@/hooks/use-push-opt-in';

const DISMISS_KEY = 'alfie:notif-banner-dismissed-at';
const DISMISS_TTL_MS = 24 * 60 * 60 * 1000; // 24h

/**
 * Persistent opt-in banner for browser notifications.
 *
 * Shown when:
 *   - user is authed
 *   - prefs are loaded
 *   - Notification.permission === 'default'  (never asked; browser will prompt)
 *   - user hasn't dismissed within the last 24h
 *
 * Click "Allow" requests permission. On grant, flips desktop_push_enabled
 * to true so usePushSubscription registers the push subscription.
 * Click "Not now" stores a 24h dismissal so the banner stops nagging
 * but eventually re-asks.
 *
 * The pref defaults to false; this banner is the primary opt-in surface,
 * so it intentionally has no permanent dismiss — only "do it" or "ask later".
 *
 * The permission state and the enable action come from `usePushOptIn`, which Home's set-up row
 * shares. The snooze below is this banner's alone: a checklist row has nothing to nag with.
 */
export function EnableNotificationsBanner() {
  const { data: user } = useCurrentUser();
  const { status, isLoading: prefsLoading, busy, enable } = usePushOptIn();

  const [snoozedUntil, setSnoozedUntil] = useState<number>(() => {
    if (typeof localStorage === 'undefined') return 0;
    return Number(localStorage.getItem(DISMISS_KEY) ?? 0);
  });

  if (!user) return null;
  if (status !== 'default') return null;
  if (prefsLoading) return null;
  if (Date.now() < snoozedUntil) return null;

  async function handleAllow() {
    const result = await enable();
    if (result !== 'granted') {
      // 'denied' or 'default' (dismissed). Snooze 24h so we don't nag.
      const until = Date.now() + DISMISS_TTL_MS;
      localStorage.setItem(DISMISS_KEY, String(until));
      setSnoozedUntil(until);
    }
  }

  function handleSnooze() {
    const until = Date.now() + DISMISS_TTL_MS;
    localStorage.setItem(DISMISS_KEY, String(until));
    setSnoozedUntil(until);
  }

  return (
    <div className="mb-3 flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-3 text-sm">
      <Bell className="h-4 w-4 shrink-0 text-muted-foreground" />
      <div className="flex-1 min-w-0">
        <p className="font-medium text-foreground">Turn on notifications</p>
        <p className="text-xs text-muted-foreground">
          Get notified about new messages and mentions, even when Alfie isn't open.
        </p>
      </div>
      <button
        type="button"
        onClick={handleAllow}
        disabled={busy}
        className="shrink-0 rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
      >
        Allow
      </button>
      <button
        type="button"
        onClick={handleSnooze}
        aria-label="Not now"
        className="shrink-0 rounded-lg p-1.5 text-muted-foreground hover:bg-secondary hover:text-foreground"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
